import React from "react";
import { motion } from "framer-motion";
import { SITE, whatsappLink } from "@/lib/site";
import { CheckCircle2, ArrowUpRight } from "lucide-react";

const credentials = [
  "10+ Years as Elite Master Trainer · Fitness First DIFC",
  "Specialist in corrective exercise & movement restoration",
  "Trains all ages and fitness levels, pain-free and joint-friendly",
  "Now coaching at Train Strength & Fitness, Dubai Mall Zabeel",
];

export const AboutFounder = () => {
  return (
    <section
      id="about"
      data-testid="about-founder-section"
      className="relative py-20 sm:py-28 lg:py-36 overflow-hidden"
    >
      <div className="aurora" style={{ background: "#0055FF", width: 600, height: 600, top: 100, right: -200, opacity: 0.25 }}></div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14 items-center relative z-10">
        {/* Left – portrait */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-5"
        >
          <div className="relative">
            {/* Soft gradient halo behind the circular portrait */}
            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[#0055FF]/30 to-[#00E5FF]/30 blur-3xl scale-105"></div>

            {/* Circular gradient border ring */}
            <div className="relative aspect-square w-full max-w-[520px] mx-auto rounded-full p-[3px] bg-gradient-to-br from-[#0055FF] via-[#00B8FF] to-[#00E5FF] shadow-[0_30px_80px_-20px_rgba(0,229,255,0.45)]">
              <div className="relative w-full h-full rounded-full overflow-hidden ring-1 ring-white/20 bg-black">
                <img
                  src={SITE.founderPhoto}
                  alt={SITE.founder}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover"
                  data-testid="founder-portrait"
                />
              </div>
            </div>

            {/* Floating badge below the portrait */}
            <div className="relative mt-6 mx-auto max-w-[360px] glass rounded-2xl p-4 flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-[#00E5FF]">Founder</div>
                <div className="font-display text-white font-bold text-lg mt-0.5">{SITE.founder}</div>
              </div>
              <div className="text-right">
                <div className="text-xs uppercase tracking-[0.18em] text-zinc-400">Based In</div>
                <div className="text-white text-sm font-semibold mt-0.5">Dubai, UAE</div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Right – story */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="lg:col-span-7"
        >
          <div className="text-xs uppercase tracking-[0.3em] text-[#00E5FF] mb-4">Meet the Founder</div>
          <h2 className="font-display text-4xl lg:text-5xl xl:text-6xl font-extrabold leading-[1.05] text-white tracking-[-0.02em]">
            A decade of coaching <br className="hidden lg:block" />
            <span className="text-gradient-blue">elite movement</span> in Dubai.
          </h2>

          <p className="mt-6 text-zinc-300/90 text-lg leading-relaxed max-w-2xl">
            {SITE.founder} is a Dubai-based corrective exercise specialist and personal
            trainer with over a decade of front-line coaching experience. After serving as
            an Elite Master Trainer at Fitness First DIFC for 10+ years, he now coaches
            high-performing clients independently at Train Strength &amp; Fitness inside
            Dubai Mall.
          </p>
          <p className="mt-4 text-zinc-400 leading-relaxed max-w-2xl">
            His method is simple, evidence-based, and results-driven, built to restore
            movement quality, reduce pain, improve posture, and build sustainable,
            joint-friendly strength for any age or fitness level.
          </p>

          <ul className="mt-8 space-y-3 max-w-xl">
            {credentials.map((c) => (
              <li key={c} className="flex items-start gap-3 text-zinc-300">
                <CheckCircle2 className="w-5 h-5 mt-0.5 text-[#00E5FF] shrink-0" />
                <span>{c}</span>
              </li>
            ))}
          </ul>

          <div className="mt-10 flex flex-wrap gap-4">
            <a
              href={whatsappLink("Hi Anoop, I'd like to book a 1:1 consultation.")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="about-cta-whatsapp"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-[#0055FF] to-[#00E5FF] text-black px-6 py-3.5 rounded-full font-semibold hover:shadow-[0_0_30px_-5px_rgba(0,229,255,0.5)] transition-shadow"
            >
              Book a 1:1 Consultation
              <ArrowUpRight className="w-4 h-4" />
            </a>
            <a
              href="#services"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full border border-white/15 text-white hover:border-[#00E5FF]/40 hover:bg-white/5 transition-colors"
              data-testid="about-cta-services"
            >
              Explore Services
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default AboutFounder;
